/**
 * FloatingWindowsProvider — multi-open, non-blocking, draggable window
 * manager for the NDS-1 dashboard.
 *
 * Semantics:
 *   • openWindow(kind, payload) → returns id. Cascades positioning so new
 *     windows don't stack directly on old ones.
 *   • Any number of windows can be open at once.
 *   • Windows never block the main board — no scrim, no backdrop, no blur.
 *   • Each window tracks its own {x, y, w, h, z, minimized}. Clicking a
 *     window bumps its z to the top. Minimized windows collapse into a
 *     bottom dock.
 *   • Persistence: intentionally NONE. Every session is a fresh desktop.
 *     The user asked for an operations console, not a browser tab reload.
 */
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

const Ctx = createContext(null);

const DEFAULT_SIZE = {
  quickEdit:     { w: 440, h: 560 },
  workbench:     { w: 900, h: 620 },
  brief_intake:  { w: 780, h: 720 },
  new_contact:   { w: 500, h: 560 },
  send_sms:      { w: 500, h: 460 },
  open_module:   { w: 480, h: 320 },
  step_coaching: { w: 560, h: 620 },
  quickForms:    { w: 460, h: 600 },
  cardConfig:    { w: 520, h: 640 },
  resident_intake: { w: 640, h: 720 },
  campus_residents: { w: 760, h: 720 },
  re_portal_as: { w: 480, h: 860 },
  pillar_board:      { w: 1180, h: 760 },
  pillar_1_inner:    { w: 640, h: 720 },
  pillar_2_vocation: { w: 640, h: 720 },
  pillar_3_spirit:   { w: 640, h: 720 },
  pillar_4_daily:    { w: 640, h: 720 },
  fin_ledger:        { w: 900, h: 760 },
  fin_paychecks:     { w: 860, h: 720 },
  fin_savings:       { w: 820, h: 700 },
  fin_penalties:     { w: 860, h: 720 },
  fin_invoicing:     { w: 940, h: 760 },
  fin_session_payouts: { w: 900, h: 740 },
  fin_rent_roll:     { w: 900, h: 740 },
  fin_program_fees:  { w: 900, h: 740 },
  fin_staff_expenses: { w: 900, h: 740 },
  fin_director_credits: { w: 900, h: 740 },
  fin_fund_requests: { w: 900, h: 740 },
  fin_payroll: { w: 980, h: 740 },
  fin_clearinghouse: { w: 980, h: 760 },
  fin_agency_distributions: { w: 920, h: 740 },
  fin_reports: { w: 1040, h: 780 },
  fin_recap: { w: 1000, h: 780 },
  fin_org_ledger: { w: 1100, h: 800 },
  fin_bank_ledger: { w: 920, h: 740 },
  par_directory: { w: 960, h: 760 },
  re_intake_form: { w: 980, h: 800 },
  re_checkins: { w: 1320, h: 800 },
  re_session: { w: 920, h: 820 },
  re_documents: { w: 900, h: 720 },
  re_conduct: { w: 900, h: 760 },
  re_case_notes: { w: 900, h: 760 },
  re_memos: { w: 900, h: 760 },
  par_memos: { w: 860, h: 760 },
  fin_resident_actions: { w: 1000, h: 720 },
  re_lifepass: { w: 980, h: 760 },
  fin_paycheck_review: { w: 1040, h: 780 },
  re_sa_controls: { w: 1000, h: 780 },
  ops_problems: { w: 960, h: 780 },
  fix_duplicate: { w: 860, h: 720 },
  fix_guided: { w: 820, h: 720 },
  docs_center: { w: 1100, h: 800 },
  docs_fields: { w: 1180, h: 860 },
  re_properties: { w: 940, h: 760 },
  re_room_assignments: { w: 920, h: 740 },
  re_incidents: { w: 960, h: 780 },
  sms_panel: { w: 980, h: 800 },
  par_assignments: { w: 900, h: 720 },
  par_referrals: { w: 940, h: 760 },
  par_sponsorships: { w: 900, h: 740 },
  par_program_stats: { w: 900, h: 760 },
  doc_template_library: { w: 920, h: 780 },
  doc_generator: { w: 980, h: 820 },
  doc_assignments: { w: 940, h: 780 },
  doc_signatures: { w: 900, h: 760 },
  doc_vault: { w: 1040, h: 780 },
  sch_calendar: { w: 1320, h: 860 },
  sch_event_form: { w: 760, h: 820 },
  sch_event_detail: { w: 440, h: 680 },
  sch_task_form: { w: 620, h: 640 },
  fin_paycheck_form: { w: 640, h: 720 },
  sch_conflicts: { w: 900, h: 760 },
  sch_audit: { w: 980, h: 780 },
  sch_availability: { w: 1180, h: 820 },
  grant_report: { w: 760, h: 720 },
  grant_tile: { w: 640, h: 640 },
};

const cascadeStep = 32;

const nextPosition = (index, w = 360, h = 500) => {
  if (typeof window === "undefined") return { x: 100, y: 100 };
  const baseX = Math.max(24, Math.round(window.innerWidth * 0.55));
  const baseY = 120;
  const x = Math.max(16, Math.min(window.innerWidth - Math.min(w, window.innerWidth) - 16, baseX + (index % 6) * cascadeStep));
  const want = baseY + (index % 6) * cascadeStep;
  const y = Math.max(12, Math.min(want, window.innerHeight - Math.min(h, window.innerHeight - 24) - 12));
  return { x, y };
};

export const FloatingWindowsProvider = ({ children }) => {
  const [windows, setWindows] = useState([]);
  const zRef = useRef(50);
  const idRef = useRef(0);
  const openCountRef = useRef(0);

  const bumpZ = () => { zRef.current += 1; return zRef.current; };

  const openWindow = useCallback((kind, payload = {}) => {
    const id = `win-${++idRef.current}`;
    const size = DEFAULT_SIZE[kind] || { w: 480, h: 500 };
    const z = bumpZ();
    if (kind === "fin_reports" || kind === "par_memos" || (kind === "fin_invoicing" && payload.invoiceId)) {  // one window: an open one moves to the new period / invoice instead of a second copy
      setWindows((ws) => {
        const w = ws.find((x) => x.kind === kind);
        if (w) return ws.map((x) => (x.id === w.id ? { ...x, payload: { ...payload, nonce: Date.now() }, z, minimized: false } : x));
        const pos = nextPosition(openCountRef.current++, size.w, size.h);
        return [...ws, { id, kind, payload, x: pos.x, y: pos.y, w: size.w, h: size.h, z, minimized: false }];
      });
      return id;
    }
    const pos = nextPosition(openCountRef.current++, size.w, size.h);
    setWindows((ws) => [...ws, { id, kind, payload, x: pos.x, y: pos.y, w: size.w, h: size.h, z, minimized: false }]);
    return id;
  }, []);

  const closeWindow = useCallback((id) => {
    setWindows((ws) => ws.filter((w) => w.id !== id));
  }, []);

  const closeByKindAndKey = useCallback((kind, keyFn) => {
    setWindows((ws) => ws.filter((w) => !(w.kind === kind && keyFn(w.payload))));
  }, []);

  const focusWindow = useCallback((id) => {
    const z = bumpZ();
    setWindows((ws) => ws.map((w) => (w.id === id ? { ...w, z, minimized: false } : w)));
  }, []);

  const updateWindow = useCallback((id, patch) => {
    setWindows((ws) => ws.map((w) => (w.id === id ? { ...w, ...patch } : w)));
  }, []);

  const minimizeWindow = useCallback((id) => {
    setWindows((ws) => ws.map((w) => (w.id === id ? { ...w, minimized: true } : w)));
  }, []);

  const restoreWindow = useCallback((id) => {
    const z = bumpZ();
    setWindows((ws) => ws.map((w) => (w.id === id ? { ...w, minimized: false, z } : w)));
  }, []);

  const closeAll = useCallback(() => setWindows([]), []);

  // Finance Report: reuse an open window (move it to the new period) instead of opening a second copy.
  const openFinReport = useCallback((payload = {}) => openWindow("fin_reports", payload), [openWindow]);

  const value = useMemo(() => ({
    windows,
    openWindow, closeWindow, closeByKindAndKey,
    focusWindow, updateWindow,
    minimizeWindow, restoreWindow,
    closeAll, openFinReport,
  }), [windows, openWindow, closeWindow, closeByKindAndKey, focusWindow, updateWindow, minimizeWindow, restoreWindow, closeAll, openFinReport]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useFloatingWindows = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useFloatingWindows must be used inside <FloatingWindowsProvider>");
  return v;
};
